import React, {useMemo, useRef, useState} from 'react';
import {classes, style} from './doppe-action-type-membership-list-widget.st.css';
import {AnimatedAutoSizedElement, listViewCreateDataSource} from '@wix/devzai-utils-react';
import {
    convertDoppeWixMembershipPlanToPlansSettings,
    DoppeActionTypeMembershipList
} from '../doppe-action-type-membership-list';
import {
    DoppeActionTypeWidgetHeader
} from '../../../components/doppe-action-type-widget-header/doppe-action-type-widget-header';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-list-view-item';
import {
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsHidden,
    doppeHideableValueIsVisible,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    doppeActionWidgetPerformWidgetContentReplacement
} from '../../../components/doppe-action-widget-utils/doppe-action-widget-utils';
import {
    DoppeActionListItemClickBehaviour,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsAvailable,
    doppeExternalMembershipPlanProviderResolveBusinessEntityReference
} from '../../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    arrayFind,
    arrayFirst,
    arraySkipTake,
    asArray,
    evaluateFunction,
    iterableMapToArray
} from '@wix/devzai-utils-common';
import {
    DoppeActionBackHeaderNew
} from '../../doppe-actions-common/doppe-action-back-header-new/doppe-action-back-header-new';
import {DoppeActionCouponView} from '../../doppe-actions-common/doppe-action-coupon-view/doppe-action-coupon-view';
import {
    useDoppeViewerLangDictionary
} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {
    DoppeActionTypeMembershipBindableWidget
} from '../../doppe-action-type-membership/doppe-action-type-membership-widget/doppe-action-type-membership-widget';
import {
    DoppeActionTypeWidgetContentLayout
} from '../../../components/doppe-action-type-widget-content-layout/doppe-action-type-widget-content-layout';
import {
    DoppeActionTypeWidgetContentSection
} from '../../../components/doppe-action-type-widget-content-section/doppe-action-type-widget-content-section';
import {FlexLayout} from '@wix/devzai-common-client';
import {DoppeActionBackHeader} from '../../doppe-actions-common/doppe-action-back-header/doppe-action-back-header';
import {
    DoppeMembershipPlansExternalDataSourceType
} from '../../../client-server-common/types/doppe-membership-plans-external-data-source';
import {DoppeAutoMembershipPlansType} from '../../../client-server-common/types/doppe-auto-membership-plan-type';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';

export const DoppeActionTypeMembershipListWidget = React.memo(function DoppeActionTypeServicesListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeMembershipList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        plans,
        layout,
        itemClickBehaviour,
        showButton,

        previewShowCoupon,
        previewShowPrice,
        previewShowDescription,
        previewShowTitle,
        previewPlanButtonText,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
        itemShowPrice,

        externalDataSource,
        externalDataSourceItemsCount,
    } = actionSettings;

    const bymoPageContextProps = useBymoPageContextProps();

    const displayedPlans = useMemo(() => {
        if (externalDataSource === null) {
            return plans;
        } else {

            const externalPlans = evaluateFunction(() => {
                switch (externalDataSource.type) {
                    case DoppeMembershipPlansExternalDataSourceType.LatestWixMembershipPlans: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoMembershipPlansList,
                            id: DoppeAutoMembershipPlansType.Latest
                        }) ?? []
                    }
                }
            })

            return arraySkipTake(externalPlans, 0, externalDataSourceItemsCount).map(doppeDtoPlan => {
                return {
                    ...convertDoppeWixMembershipPlanToPlansSettings(doppeDtoPlan),
                    // We override the provider and the id in the provider since the values that are fetched
                    // here are already synced with the external provider.
                    membershipProvider: null,
                    membershipProviderId: null,
                } satisfies DoppeActionTypeMembershipList.PlanSettings
            });
        }
    }, [externalDataSource, externalDataSourceItemsCount, plans, bymoPageContextProps])

    const [expandedPlanId, setExpandedPlanId] = useState<DoppeActionTypeMembershipList.PlanSettings['id'] | null>(null);

    const currentPlanToShow = useMemo(
        () => displayedPlans.find(plan => plan.id === expandedPlanId) ?? null,
        [displayedPlans, expandedPlanId]);

    const existingPlans = useMemo(() => {
        return iterableMapToArray(displayedPlans, (service, skip) => {

            const {
                membershipProviderId,
                membershipProvider
            } = service;

            if (membershipProvider !== null && membershipProviderId !== null) {
                const boundService = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalMembershipPlanProviderResolveBusinessEntityReference(membershipProvider, membershipProviderId)
                );

                if (doppeBusinessEntityDtoIsAvailable(boundService)) {
                    return {
                        ...service,
                        businessEntityDto: boundService
                    }
                } else {
                    return skip;
                }
            } else {
                return {
                    ...service,
                    businessEntityDto: undefined
                }
            }
        })
    }, [displayedPlans, bymoPageContextProps]);

    const plansListViewDataSource = useMemo(() => {
        return listViewCreateDataSource(existingPlans, {
            idFunction: item => item.id
        });
    }, [existingPlans]);


    const rootElementRef = useRef(null);
    const viewrLang = useDoppeViewerLangDictionary();

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
            ref={rootElementRef}
        >
            {
                !currentPlanToShow ?
                    <DoppeActionTypeWidgetHeader
                        className={style(classes.header, {})}
                        actionContext={actionContext}
                        withBorderBottom={false}
                    />
                    :
                    null
            }
            <AnimatedAutoSizedElement
                durationMilliseconds={200}
            >
                <div>
                    {
                        !currentPlanToShow ?
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                actionContext={actionContext}
                                layout={layout}
                                hasSeeMoreButton={showButton}
                                onItemClick={eventData => {

                                    const itemId = eventData.id;

                                    switch (itemClickBehaviour) {
                                        case DoppeActionListItemClickBehaviour.PreviewItem: {
                                            doppeActionWidgetPerformWidgetContentReplacement(
                                                rootElementRef.current,
                                                () => {
                                                    setExpandedPlanId(itemId)
                                                }
                                            )
                                            break;
                                        }
                                        case DoppeActionListItemClickBehaviour.OpenLink: {
                                            const [service, serviceIndex] = arrayFind(existingPlans, item => item.id === itemId);

                                            if (service) {

                                                if (doppeHideableValueIsVisible(service.couponCode)) {
                                                    doppeActionWidgetPerformWidgetContentReplacement(rootElementRef.current, () => {
                                                        setExpandedPlanId(itemId)
                                                    })
                                                } else {
                                                    actionContext.reportGoalAchieved(itemId, serviceIndex);
                                                    actionContext.navigateToUrl(service.membershipPageLink, true);
                                                }
                                            }

                                            break;
                                        }
                                    }
                                }}
                                renderItem={(dataItem, layout, index) => {
                                    const {
                                        membershipImages,
                                        membershipTitle,
                                        membershipDescription,
                                        priceData,
                                        ribbon,
                                    } = dataItem;

                                    const isHidden = doppeHideableValueIsHidden(priceData);
                                    const priceDataValue = doppeHideableValueGetValue(priceData);

                                    return (
                                        <DoppeActionTypeListViewItem
                                            actionContext={actionContext as DoppeViewerActionContext<any>}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={membershipTitle}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(membershipDescription, '') ? membershipDescription : ''}
                                            image={membershipImages?.[0] ?? null}
                                            layout={layout}
                                            price={isHidden || !itemShowPrice ? '' : priceDataValue.actualPrice}
                                            isOneItem={plansListViewDataSource.length === 1}
                                            isLink={itemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink}
                                            ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? doppeHideableValueGetValue(ribbon) : undefined}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={plansListViewDataSource}
                            />
                            :
                            <>
                                {
                                    itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItem ?
                                        <DoppeActionTypeWidgetContentLayout>
                                            <DoppeActionTypeWidgetContentSection
                                                className={style(classes.content, {}, FlexLayout.column.default)}
                                            >
                                                <DoppeActionBackHeader
                                                    onBackClick={() => {
                                                        setExpandedPlanId(null);
                                                    }}
                                                    layout={layout}
                                                />
                                                <DoppeActionTypeMembershipBindableWidget
                                                    actionSettings={{
                                                        membershipImages: currentPlanToShow.membershipImages,
                                                        membershipPageLink: currentPlanToShow.membershipPageLink,
                                                        membershipType: currentPlanToShow.membershipType,
                                                        membershipBenefits: currentPlanToShow.membershipBenefits,
                                                        ribbon: currentPlanToShow.ribbon,
                                                        membershipProvider: currentPlanToShow.membershipProvider ?? null,
                                                        membershipProviderId: currentPlanToShow.membershipProviderId ?? null,
                                                        couponCode: doppeHideableValueToggleHidden(
                                                            currentPlanToShow.couponCode,
                                                            isHidden => isHidden ? true : !previewShowCoupon
                                                        ),
                                                        membershipDescription: doppeHideableValueToggleHidden(
                                                            currentPlanToShow.membershipDescription,
                                                            isHidden => isHidden ? true : !previewShowDescription
                                                        ),
                                                        membershipTitle: doppeHideableValueToggleHidden(
                                                            currentPlanToShow.membershipTitle,
                                                            isHidden => isHidden ? true : !previewShowTitle
                                                        ),
                                                        linkButtonText: doppeHideableValueGetVisibleValue(
                                                            currentPlanToShow.linkButtonText,
                                                            previewPlanButtonText
                                                        ),
                                                        priceData: doppeHideableValueToggleHidden(
                                                            currentPlanToShow.priceData,
                                                            isHidden => isHidden ? true : !previewShowPrice
                                                        ),
                                                    }}
                                                    reportGoalAchieved={() => {
                                                        actionContext.reportGoalAchieved(
                                                            currentPlanToShow?.id,
                                                            existingPlans.findIndex(item => item.id === currentPlanToShow?.id)
                                                        );
                                                    }}
                                                    actionId={actionContext.action.id}
                                                />
                                            </DoppeActionTypeWidgetContentSection>
                                        </DoppeActionTypeWidgetContentLayout>
                                        :
                                        <>
                                            <DoppeActionBackHeaderNew
                                                onBackClick={() => {
                                                    setExpandedPlanId(null);
                                                }}
                                                layout={layout}
                                            />
                                            <DoppeActionCouponView
                                                couponCode={doppeHideableValueGetValue(currentPlanToShow.couponCode)}
                                                couponImage={currentPlanToShow.membershipImages ? arrayFirst(asArray(currentPlanToShow.membershipImages)) : undefined}
                                                linkUrl={currentPlanToShow.membershipPageLink}
                                                couponButtonText={viewrLang.continueToWebsite}
                                                description={viewrLang.couponDescription}
                                                reportGoalAchieved={() => {
                                                    actionContext.reportGoalAchieved(
                                                        currentPlanToShow?.id,
                                                        existingPlans.findIndex(item => item.id === currentPlanToShow?.id)
                                                    );
                                                }}
                                                actionId={actionContext.action.id}
                                            />
                                        </>
                                }
                            </>
                    }
                </div>
            </AnimatedAutoSizedElement>
        </div>
    );
});


